import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';

interface DeviceRequest {
  userCode: string;
  clientName: string;
  scope: string;
  expiresAt: string;
}

/**
 * Approving a device code. This is the human act that an agent credential's consent is later measured
 * against, so it deliberately says which client is asking and never approves anything automatically,
 * even when the code arrives prefilled in the URL.
 */
export function Device() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [request, setRequest] = useState<DeviceRequest | null>(null);
  const [outcome, setOutcome] = useState<'approved' | 'denied' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function look(value: string) {
    setError(null);
    setBusy(true);
    try {
      setRequest(await api<DeviceRequest>(`/api/device/${encodeURIComponent(value)}`));
    } catch (err) {
      setRequest(null);
      setError(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  // A code in the link is looked up, never acted on: the person still has to press the button.
  useEffect(() => {
    const prefilled = params.get('code');
    if (prefilled) void look(prefilled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function decide(decision: 'approve' | 'deny') {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/device/${encodeURIComponent(request.userCode)}/${decision}`, { method: 'POST' });
      setOutcome(decision === 'approve' ? 'approved' : 'denied');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  if (outcome === 'approved') {
    return (
      <>
        <PageHeader title="Device signed in" />
        <div className="card measure">
          <div className="ok" style={{ marginBottom: 0 }}>
            You can go back to your terminal. It will pick this up within a few seconds.
          </div>
        </div>
      </>
    );
  }

  if (outcome === 'denied') {
    return (
      <>
        <PageHeader title="Declined" />
        <div className="card measure">
          <p style={{ margin: 0 }}>Nothing was granted. If you did not start this, no action is needed — the code is now dead.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Connect a device" />
      <div className="card measure">
        {request ? (
          <>
            <p className="lede">
              <strong>{request.clientName}</strong> is asking to sign in as you.
            </p>
            <p>
              Code <span className="mono">{request.userCode}</span>
            </p>
            <div className="notice">
              It will be able to open the customer orgs you already hold a grant on, from a terminal. It cannot grant
              itself anything new, and you can end it at any time from your account page.
            </div>
            <p className="small muted">
              Only approve this if you have just started it yourself. If the code came from someone else, decline it.
            </p>
            {error && <div className="alert">{error}</div>}
            {/* Two equal blocks side by side: declining is as much an answer as approving, so it is not
                made the smaller, harder target. Approve is still the filled one. */}
            <div className="decide">
              <button type="button" disabled={busy} onClick={() => decide('approve')}>
                Approve
              </button>
              <button type="button" className="secondary" disabled={busy} onClick={() => decide('deny')}>
                Decline
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="lede">Type the code shown in your terminal.</p>
            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void look(code);
              }}
            >
              <label>
                Code
                <input
                  id="device-code"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="XXXX-XXXX"
                  className="mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              {error && <div className="alert">{error}</div>}
              <button type="submit" className="block" disabled={busy || code.trim().length < 8}>
                {busy ? 'Looking it up…' : 'Continue'}
              </button>
            </form>
          </>
        )}
      </div>
    </>
  );
}
